// A custom hook to perform GraphQL queries
import { useState, useEffect } from 'react';
import { GraphQLClient, Result } from 'graphql-hooks';

// Define the types for your query results and variables
// Adjust these types based on your actual data and query variables
interface QueryResult {
  [key: string]: any; // Example placeholder, replace with your actual data structure
}

interface QueryVariables {
  [key: string]: any; // Example placeholder, replace with your actual query variables structure
}

// A custom hook to perform GraphQL queries, typed with TypeScript
export default function useGQLQueryWithClient<T = QueryResult, V = QueryVariables>(client: GraphQLClient, query: string, variables: V = {} as V, prohibit : boolean = false): { loading: boolean; error: Error | null; data: Result<unknown, object>; } {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<Result<unknown, object>>({});

  useEffect(() => {
    setLoading(true);
    if (prohibit) return;
    client.request({ query, variables })
      .then(responseData => {
        console.log({responseData})
        setData(responseData);
        setLoading(false);
      })
      .catch(error => {
        setError(error);
        setLoading(false);
      });
  }, [prohibit, JSON.stringify(query), JSON.stringify(variables)]); // Ensuring deep comparison for objects

  return { loading, error, data };
}

export function useMultipleGQLQueryWithClient<T = QueryResult, V = QueryVariables>(client: GraphQLClient, queries: Array<string>, variables: V = {} as V): { loading: boolean; error: Error | null; data: Array<Result<unknown, object>>; } {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<Array<Result<unknown, object>>>([]);

  useEffect(() => {
    async function whyUseEffectWontAcceptAsyncCallbacks() {
      setLoading(true);
      const data : Array<Result<unknown, object>> = [];
      try {
        for (const query of queries) {
          const responseData = await client.request({ query, variables });    
          data.push(responseData); 
        }
        setData(data);
        setLoading(false);
      } catch (error) {
        setError(error as Error);
        setLoading(false);
      }
    }
    whyUseEffectWontAcceptAsyncCallbacks();
  }, [JSON.stringify(queries), JSON.stringify(variables)]); // Ensuring deep comparison for objects

  return { loading, error, data };
}

export function useGQLQueryMultipleRecordsWithClient<T = QueryResult, V = QueryVariables>(client: GraphQLClient, queryGenerator: ({first, skip} : {first : number, skip : number}) => string, stepSize : number, variables: V = {} as V, prohibit : boolean = false): { loading: boolean; error: Error | null; data: any[]; } {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<any[]>([]);

  const records = [];

  useEffect(() => {
    if (prohibit) return;

    async function body() : Promise<any[]> {
      const allResults : any[] = [];

      let everyDataDownloaded = false;
      let first = stepSize;
      let skip = 0;
      while (!everyDataDownloaded) {
        const responseData = await client.request({ query: queryGenerator({first, skip}), variables })
        const { data } = responseData as { data : any};
        const keyName = Object.keys(data)[0];
        const results = data[keyName] as any[];
        everyDataDownloaded = results.length < stepSize;
        allResults.push(...results);
        skip += stepSize;
      }
      
      return allResults;
      
    }
    setLoading(true);

    body()
      .then((allResults) => {
        setData(allResults);
        setLoading(false);
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
    
  }, [prohibit, queryGenerator({first: 0, skip: 0}), JSON.stringify(variables)]); // Ensuring deep comparison for objects

  return { loading, error, data };
}